import { APP_INITIALIZER, Provider } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  INTERNAL_LINK_HOSTS_RESOLVER,
  INTERNAL_LINK_RESOURCE_OPENER,
  InternalLinkService,
  ResourceLinkType
} from '@bling-fe/shared/services';
import {
  APP_TENANT_CONFIG,
  TenantConfig
} from '@bling-fe/shared/tenant-config';
import { Tenant } from '../../core/declarations/tenant.interface';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import { DeepLinkService } from './deep-link.service';
import { DocumentOpenerService } from './document-opener.service';

/**
 * Single wiring point for everything link-related in an app:
 *
 * - InternalLinkService's document-level click interceptor (in-app routing
 *   for internal links in rendered content)
 * - DeepLinkService's `appUrlOpen` listener (universal links / app links)
 * - the tenant host resolver both rely on to recognise the current tenant's
 *   real domains (needed on native builds where window.location.host is the
 *   local WebView origin, and on web for alias hosts like wink.abz.ch next
 *   to abz.flink.coop)
 */
export function provideLinkHandling(): Provider[] {
  return [
    {
      provide: INTERNAL_LINK_HOSTS_RESOLVER,
      deps: [Store, APP_TENANT_CONFIG],
      useFactory: (store: Store, tenantConfigs: TenantConfig[]) => {
        let currentTenant: Tenant | 'loading' | null = null;
        store.select(getCurrentTenant).subscribe(tenant => {
          currentTenant = tenant;
        });

        return (): string[] => {
          const tenant = currentTenant;
          if (!tenant || tenant === 'loading') return [];

          const hosts: string[] = [];
          try {
            hosts.push(new URL(tenant.base_url).host);
          } catch {
            // ignore malformed base_url
          }

          const config = tenantConfigs.find(
            c => c.subdomain === tenant.subdomain
          );
          if (config) {
            hosts.push(...config.urls.staging, ...config.urls.production);
          }
          return hosts;
        };
      }
    },
    {
      provide: INTERNAL_LINK_RESOURCE_OPENER,
      deps: [DocumentOpenerService],
      useFactory:
        (documentOpener: DocumentOpenerService) =>
        (ref: { type: ResourceLinkType; id: number }): boolean => {
          if (ref.type !== 'document') return false;
          documentOpener.openById(ref.id);
          return true;
        }
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [InternalLinkService, DeepLinkService],
      useFactory:
        (internalLinks: InternalLinkService, deepLinks: DeepLinkService) =>
        () => {
          internalLinks.init();
          deepLinks.init();
        }
    }
  ];
}
